/** Fit-to-width is the floor — zoom only magnifies the score. */
export const SCORE_ZOOM_MIN = 1;
export const SCORE_ZOOM_MAX = 4;

/** The score column may grow with zoom up to this fraction of the browser. */
export const SCORE_ZOOM_GROW_VW = 0.9;

/**
 * Published on `:root` by `useScoreZoom` so the fixed side toggles
 * can sit just outside the (possibly grown) column without React
 * re-renders on every pinch frame.
 */
export const SCORE_COLUMN_WIDTH_VAR = '--score-column-width';

/** Wheel sensitivity. Mouse notches (~100px) step ~20%; trackpads finer. */
const WHEEL_SENSITIVITY = 0.002;

export const clampScoreZoom = (
  scale: number,
  min = SCORE_ZOOM_MIN,
  max = SCORE_ZOOM_MAX
): number => Math.min(max, Math.max(min, scale));

export const scaleFromPinch = (
  startScale: number,
  startDistance: number,
  currentDistance: number
): number => {
  if (startDistance <= 0) return clampScoreZoom(startScale);
  return clampScoreZoom(startScale * (currentDistance / startDistance));
};

export const scaleFromWheel = (currentScale: number, deltaY: number): number =>
  clampScoreZoom(currentScale * Math.exp(-deltaY * WHEEL_SENSITIVITY));

/**
 * Width of the white score column at a given zoom. Grows from the
 * resting `max-w-7xl` width toward `growFraction` of the browser,
 * then stops so further zoom scrolls inside. Never shrinks a column
 * that is already at/past the cap (phone-width `w-full`).
 */
export const columnWidthAfterZoom = ({
  baseWidth,
  scale,
  viewportWidth,
  growFraction = SCORE_ZOOM_GROW_VW,
}: {
  baseWidth: number;
  scale: number;
  viewportWidth: number;
  growFraction?: number;
}): number => {
  const cap = viewportWidth * growFraction;
  if (baseWidth <= 0 || baseWidth >= cap) return baseWidth;
  return Math.min(cap, baseWidth * scale);
};

/** Unscaled content point currently under a screen focal (client coords). */
export const unscaledPointUnderFocal = ({
  visualLeft,
  visualTop,
  focalX,
  focalY,
  scale,
}: {
  visualLeft: number;
  visualTop: number;
  focalX: number;
  focalY: number;
  scale: number;
}): { x: number; y: number } => ({
  x: (focalX - visualLeft) / (scale || 1),
  y: (focalY - visualTop) / (scale || 1),
});

/**
 * How far to scroll so `unscaled` stays under `focal` after a
 * top-left `scale()` (no translate). Used when a pinch or wheel
 * gesture commits layout.
 */
export const scrollDeltaToKeepPoint = ({
  visualLeft,
  visualTop,
  unscaledX,
  unscaledY,
  scale,
  focalX,
  focalY,
}: {
  visualLeft: number;
  visualTop: number;
  unscaledX: number;
  unscaledY: number;
  scale: number;
  focalX: number;
  focalY: number;
}): { x: number; y: number } => ({
  x: visualLeft + unscaledX * scale - focalX,
  y: visualTop + unscaledY * scale - focalY,
});

/**
 * Keep a content point under the pinch midpoint by translating, instead
 * of scrolling the document. iPhone Safari shakes if we `scrollTo` or
 * grow the sizer on every touchmove (visualViewport / URL-bar fight).
 */
export const pinchFollowTranslate = ({
  midX,
  midY,
  originX,
  originY,
  scale,
  layoutLeft,
  layoutTop,
}: {
  midX: number;
  midY: number;
  originX: number;
  originY: number;
  scale: number;
  layoutLeft: number;
  layoutTop: number;
}): { x: number; y: number } => ({
  x: midX - layoutLeft - originX * scale,
  y: midY - layoutTop - originY * scale,
});
